import React, { useState } from 'react';
import { Calculator, X, Loader2, Phone, ArrowRight, Clock, DollarSign, Users, Wrench, AlertCircle } from 'lucide-react';
import { getProjectEstimate } from '../lib/openai';

export function AIEstimateButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [estimate, setEstimate] = useState<{
    laborHours: number;
    materialCost: number;
    teamSize: number;
    totalCost: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const result = await getProjectEstimate({
        service: 'General Project',
        description: message,
        hasMaterials: false,
        projectSize: 'Medium',
        timeline: 'Within 3-6 Months',
        propertyType: 'Single Family Home'
      });
      setEstimate(result);
    } catch (error) {
      setError('Unable to generate estimate. Call (914) 960-6433 for Estimate');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setMessage('');
    setEstimate(null);
    setError(null);
  };

  return (
    <>
      {/* Floating Button - Always Bouncing */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 bg-gradient-to-r from-yellow-400 to-yellow-500 text-zinc-900 px-4 py-3 sm:px-6 sm:py-4 rounded-full shadow-xl hover:scale-105 transition-all duration-300 z-50 group animate-bounce flex items-center gap-2"
      >
        <Calculator className="h-5 w-5 sm:h-6 sm:w-6 group-hover:rotate-12 transition-transform duration-300 flex-shrink-0" />
        <span className="text-sm sm:text-base font-medium whitespace-nowrap">Get an Estimate Online</span>
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-50 animate-fade-in">
          <div className="bg-gradient-to-b from-zinc-900 to-zinc-900/95 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto border border-zinc-800/50">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-zinc-900 to-zinc-900/95 p-4 sm:p-6 border-b border-zinc-800/50 flex justify-between items-center backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-400/10 p-2 rounded-lg">
                  <Calculator className="h-5 w-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-light text-zinc-100">AI Project Estimator</h2>
                  <p className="text-xs text-zinc-400">Get an instant estimate</p>
                </div>
              </div>
              <button 
                onClick={handleClose} 
                className="text-zinc-400 hover:text-zinc-200 p-2 hover:bg-zinc-800/50 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-6">
              {/* Phone Call Banner */}
              <div className="flex items-start gap-3 bg-gradient-to-r from-yellow-400/10 to-transparent p-4 rounded-xl border border-yellow-400/20">
                <Phone className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-zinc-300 text-sm">
                    For the most accurate estimate, call{' '}
                    <a href="tel:9149606433" className="text-yellow-400 hover:text-yellow-300 transition-colors font-medium">
                      (914) 960-6433
                    </a>
                  </p>
                  <p className="text-zinc-500 text-xs">Available Monday - Friday, 9 AM - 6 PM EST</p>
                </div>
              </div>

              {/* Input Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="description" className="block text-sm text-zinc-400">
                    Project Description
                  </label>
                  <textarea
                    id="description"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Please describe your project in detail. For example:
• Type of project (bathroom remodel, kitchen renovation)
• Scope of work (what needs to be done)
• Room dimensions or size
• Special requirements or preferences"
                    className="w-full bg-zinc-800/30 border border-zinc-700 rounded-xl p-4 text-zinc-200 placeholder:text-zinc-500 min-h-[120px] focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 transition-all duration-200"
                    required
                  />
                </div>


                <button
                  type="submit"
                  disabled={isLoading || !message}
                  className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-zinc-900 py-4 rounded-xl flex items-center justify-center gap-2 hover:from-yellow-500 hover:to-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isLoading ? (
                    <>
                      {countdown > 0 ? (
                        <>
                          <div className="relative">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <div className="absolute -top-1 -right-1 bg-yellow-600 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                              {countdown}
                            </div>
                          </div>
                          <span>Analyzing Project...</span>
                        </>
                      ) : (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          <span>Calculating Estimate...</span>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Calculator className="h-5 w-5" />
                      Calculate Estimate
                    </>
                  )}
                </button>
              </form>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-3 text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-xl p-4">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Results */}
              {estimate && (
                <div className="space-y-4 animate-fade-in">
                  <div className="flex items-center justify-between border-b border-zinc-800/50 pb-4">
                    <h3 className="text-xl text-zinc-100">Project Estimate</h3>
                    <span className="text-xs text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-full">
                      AI-Generated
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-zinc-800/30 to-zinc-800/10 rounded-xl p-4 border border-zinc-700/50 hidden">
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="h-4 w-4 text-yellow-400" />
                        <div className="text-yellow-400 text-xs">Labor Hours</div>
                      </div>
                      <div className="text-xl text-zinc-100 mb-1">{estimate?.laborHours || 0} hours</div>
                      <div className="text-zinc-500 text-xs">Estimated duration</div>
                    </div>

                    <div className="bg-gradient-to-br from-zinc-800/30 to-zinc-800/10 rounded-xl p-4 border border-zinc-700/50">
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="h-4 w-4 text-yellow-400" />
                        <div className="text-yellow-400 text-xs">Team Size</div>
                      </div>
                      <div className="text-xl text-zinc-100 mb-1">{estimate?.teamSize || 0} people</div>
                      <div className="text-zinc-500 text-xs">Including specialists</div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-zinc-800/30 to-zinc-800/10 rounded-xl p-4 border border-zinc-700/50">
                    <div className="flex items-center gap-2 mb-3">
                      <Wrench className="h-4 w-4 text-yellow-400" />
                      <div className="text-yellow-400 text-xs">Materials & Equipment</div>
                    </div>
                    <div className="text-xl text-zinc-100 mb-1">${estimate?.materialCost?.toLocaleString() || '0'}</div>
                    <div className="text-zinc-500 text-xs">Materials and supplies</div>
                  </div>

                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <DollarSign className="h-4 w-4 text-zinc-900" />
                      <div className="text-zinc-900 text-xs font-medium">Total Estimate</div>
                    </div>
                    <div className="text-2xl text-zinc-900 mb-1">${estimate?.totalCost?.toLocaleString() || '0'}</div>
                    <div className="text-zinc-900/75 text-xs">Professional estimate</div>
                  </div>

                  <div className="bg-gradient-to-br from-zinc-800/30 to-zinc-800/10 rounded-xl p-4 border border-zinc-700/50">
                    <div className="flex items-start gap-3 mb-4">
                      <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div className="space-y-2">
                        <p className="text-zinc-400 text-sm">
                          This is an AI-generated estimate and may not reflect the final cost of your project. 
                          Actual costs can vary based on specific requirements, material choices, and site conditions.
                        </p>
                      </div>
                    </div>
                    <a 
                      href="tel:9149606433"
                      className="inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition-colors group"
                    >
                      <Phone className="h-5 w-5" />
                      <span className="text-sm font-medium">Call (914) 960-6433 for accurate estimate</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}